/**
 * CypAdhdSsiSideNavbar — the SSI's item list.
 *
 * What each row SAYS matches the adult ADHD symptom list: a short prefix
 * ("A 1", "H/I 1") with the full symptom wording in the same dark hover
 * tooltip the adult list uses, not the symptom name and a DSM code side by
 * side. Areas of impairment keep their titles, because those titles are
 * already the short adult-style labels.
 *
 * How each row LOOKS matches the Adult Autism modules and the CYP Clinical
 * Interview: ModularNavRow (numbered or lettered badge, emerald once
 * decided, brand colour on the open item). The gray card and violet left
 * rule from the adult ADHD list are not used.
 *
 * The box itself has no top margin and no h-full. This page sits in a
 * fixed-height row, and a margin on top of a full-height box used to hang
 * past the bottom and make the page scroll.
 */

import React from 'react';
import { Tooltip } from 'react-tooltip';
import { ModularNavRow } from '../../../../components/autismModular/ModularSideNavbar';

// Same dark bubble as the adult ADHD symptom list, so a short prefix
// ("A 7") explains itself as the full symptom wording on hover. It sits
// to the right of the compact column, where the list is a vertical strip.
const SYMPTOM_TOOLTIP_STYLE = {
  maxWidth: '250px',
  whiteSpace: 'normal',
  backgroundColor: 'rgba(34, 34, 34, 1)',
  borderRadius: '10px',
  color: '#fff',
  fontWeight: 'bold',
};

export default function CypAdhdSsiSideNavbar({
  title,
  items,
  activeIndex,
  onSelect,
  mode = 'compact',
}) {
  const isCompact = mode === 'compact';

  return (
    <div
      className={`flex-shrink-0 border-r border-gray-200 flex flex-col min-h-0 ${
        isCompact
          ? 'px-2 w-max min-w-[72px] max-w-[7.5rem] items-center'
          : 'px-4 min-w-[260px] max-w-[280px]'
      }`}
    >
      {!isCompact && title && (
        <h3 className="text-gray-900 text-base font-bold leading-tight mb-2 text-left">
          {title}
        </h3>
      )}
      {/* The list scrolls inside the column once it is taller than the row,
          so eighteen symptoms stay reachable without growing the page. */}
      <div className={`flex flex-col min-h-0 flex-1 overflow-y-auto ${
        isCompact ? 'gap-2 items-center py-2' : 'gap-0.5'
      }`}
      >
        {items.map((item, index) => {
          // Compact symptom rows hide the wording, so the hover bubble
          // carries it. Expanded area rows already show their title.
          const tooltipId = isCompact && item.title
            ? `cyp-ssi-symptom-${item.key}`
            : null;
          return (
            <div
              key={item.key}
              className={isCompact ? 'w-full flex justify-center' : 'w-full'}
              data-tooltip-id={tooltipId || undefined}
            >
              <ModularNavRow
                badge={item.badge}
                label={item.label}
                fullTitle={item.fullTitle || item.label}
                title={item.title || item.fullTitle || item.label}
                nativeTitle={!tooltipId}
                status={item.isComplete ? 'completed' : 'default'}
                isActive={activeIndex === index}
                showLabel={!isCompact}
                badgeSize={isCompact ? 'md' : 'sm'}
                onClick={() => onSelect(index)}
              />
              {tooltipId && (
                <Tooltip
                  id={tooltipId}
                  place="right"
                  style={SYMPTOM_TOOLTIP_STYLE}
                  content={item.title}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
